import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageContainer from "./layout/PageContainer.js";
import { getNetlifyFunctionsBase } from "../utils/netlifyFunctions.js";
import { getActionCategoryMeta, inferActionCategory } from "../constants/actionCategories.js";
import ActionCategoryIcon from "./ActionCategoryIcon.js";

const STATUS_LABELS = {
  open: "Open",
  not_yet: "Not yet",
  done: "Done",
  changed: "Changed my mind"
};

function formatDue(dueAt) {
  if (!dueAt) return "No due date";
  try {
    return new Date(dueAt).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  } catch (_) {
    return "No due date";
  }
}

function formatSessionDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  } catch (_) {
    return "";
  }
}

const ActionsPage = ({ verifiedEmail }) => {
  const [open, setOpen] = useState([]);
  const [closed, setClosed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [noticeById, setNoticeById] = useState({});
  const [showNoticeFor, setShowNoticeFor] = useState(null);

  const load = useCallback(async () => {
    if (!verifiedEmail) {
      setOpen([]);
      setClosed([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/getUserActions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: verifiedEmail })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Unable to load actions.");
      setOpen(Array.isArray(data.open) ? data.open : []);
      setClosed(Array.isArray(data.closed) ? data.closed : []);
    } catch (err) {
      setError(err?.message || "Unable to load actions.");
    } finally {
      setLoading(false);
    }
  }, [verifiedEmail]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (insightId, status, extra = {}) => {
    setBusyId(insightId);
    try {
      const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/updateInsightUserAction`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          insightId,
          email: verifiedEmail,
          status,
          ...extra
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Unable to update action.");
      if (status === "done") setShowNoticeFor(insightId);
      await load();
    } catch (err) {
      setError(err?.message || "Unable to update action.");
    } finally {
      setBusyId(null);
    }
  };

  const saveNoticed = async (insightId) => {
    const noticed = noticeById[insightId] || "";
    await updateStatus(insightId, "done", { noticed });
    setShowNoticeFor(null);
  };

  const renderSession = (item) => {
    const when = formatSessionDate(item.sessionCreatedAt || item.createdAt);
    if (!item.sessionPreview && !when) return null;
    return (
      <div className="actions-list__session">
        <p className="actions-list__session-label">
          Related session
          {item.sessionMode ? ` · ${item.sessionMode}` : ""}
          {when ? ` · ${when}` : ""}
        </p>
        {item.sessionPreview ? <p className="actions-list__session-preview">{item.sessionPreview}</p> : null}
        <Link className="actions-list__session-link" to="/history">
          View in History
        </Link>
      </div>
    );
  };

  if (!verifiedEmail) {
    return (
      <PageContainer className="actions-page">
        <h1 className="actions-page__title">Actions</h1>
        <p>
          <Link to="/login">Sign in</Link> to review actions from your reflections.
        </p>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="actions-page">
      <h1 className="actions-page__title">Actions</h1>
      <p className="actions-page__lead">
        Track the next step you chose after a reflection. Statuses stay practical: Done, Not yet, or Changed my mind.
      </p>
      <details className="actions-learn-more">
        <summary>Learn more about actions</summary>
        <div className="actions-learn-more__body">
          <p>
            An action is one concrete response you choose after examining an interpretation—not a mood goal or a streak.
          </p>
          <p>
            Use <strong>Done</strong> when you completed the step, <strong>Not yet</strong> when you still intend to, and{" "}
            <strong>Changed my mind</strong> when the step no longer fits the evidence.
          </p>
          <p>Reminders never include your reflection text in the notification preview.</p>
        </div>
      </details>
      {loading ? <p>Loading actions…</p> : null}
      {error ? <p className="login-error">{error}</p> : null}
      {!loading && open.length === 0 && closed.length === 0 ? (
        <p>
          No actions yet. After a Quick Clarity insight, you can log one optional next step.{" "}
          <Link to="/">Start with Quick Clarity</Link>.
        </p>
      ) : null}
      {open.length > 0 ? (
        <section className="actions-section" aria-labelledby="actions-open">
          <h2 id="actions-open" className="actions-section__title">
            Open Action Item
          </h2>
          <ul className="actions-list">
            {open.map((item) => {
              const category = item.category || inferActionCategory(item.text);
              const categoryMeta = getActionCategoryMeta(category);
              return (
                <li key={item.insightId} className="actions-list__item">
                  <div className="actions-list__head">
                    <ActionCategoryIcon category={category} className="actions-list__icon" />
                    <div>
                      <p className="actions-list__category">{categoryMeta.label}</p>
                      <p className="actions-list__text">{item.text}</p>
                    </div>
                  </div>
                  {renderSession(item)}
                  <p className="actions-list__meta">
                    Due {formatDue(item.due_at)} · {STATUS_LABELS[item.status] || item.status}
                  </p>
                  <div className="actions-list__row">
                    <button
                      type="button"
                      className="qc-confirm-btn qc-confirm-btn--primary"
                      disabled={busyId === item.insightId}
                      onClick={() => updateStatus(item.insightId, "done")}
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      className="qc-confirm-btn"
                      disabled={busyId === item.insightId}
                      onClick={() =>
                        updateStatus(item.insightId, "not_yet", {
                          due_preset: "three_days",
                          reminder_opt_in: !!item.reminder_opt_in
                        })
                      }
                    >
                      Not yet
                    </button>
                    <button
                      type="button"
                      className="qc-confirm-btn"
                      disabled={busyId === item.insightId}
                      onClick={() => updateStatus(item.insightId, "changed")}
                    >
                      Changed my mind
                    </button>
                  </div>
                  {showNoticeFor === item.insightId ||
                  (item.status === "done" && showNoticeFor === item.insightId) ? (
                    <div className="actions-noticed">
                      <p>Action completed.</p>
                      <label htmlFor={`noticed-${item.insightId}`}>Add what you noticed (optional)</label>
                      <textarea
                        id={`noticed-${item.insightId}`}
                        rows={3}
                        value={noticeById[item.insightId] || ""}
                        onChange={(e) =>
                          setNoticeById((prev) => ({ ...prev, [item.insightId]: e.target.value }))
                        }
                      />
                      <button type="button" className="qc-confirm-btn" onClick={() => saveNoticed(item.insightId)}>
                        Save note
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
      {closed.length > 0 ? (
        <section className="actions-section" aria-labelledby="actions-closed">
          <h2 id="actions-closed" className="actions-section__title">
            Completed
          </h2>
          <ul className="actions-list">
            {closed.map((item) => {
              const category = item.category || inferActionCategory(item.text);
              const categoryMeta = getActionCategoryMeta(category);
              return (
                <li key={item.insightId} className="actions-list__item actions-list__item--closed">
                  <div className="actions-list__head">
                    <ActionCategoryIcon category={category} className="actions-list__icon" />
                    <div>
                      <p className="actions-list__category">{categoryMeta.label}</p>
                      <p className="actions-list__text">{item.text}</p>
                    </div>
                  </div>
                  {renderSession(item)}
                  <p className="actions-list__meta">
                    {STATUS_LABELS[item.status] || item.status}
                    {item.due_at ? ` · was due ${formatDue(item.due_at)}` : ""}
                  </p>
                  {item.noticed ? <p className="actions-list__noticed">Noticed: {item.noticed}</p> : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </PageContainer>
  );
};

export default ActionsPage;
